import React, { useState } from 'react';
import {
  Box, Typography, Stack, Paper, Table, TableHead, TableRow, TableCell, Chip, IconButton, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Select, FormControl,
  InputLabel, CircularProgress, Alert, Snackbar, Tooltip, Switch,
  FormControlLabel, InputAdornment, Card, CardContent, Avatar,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RoomServiceIcon from '@mui/icons-material/RoomService';
import PercentIcon from '@mui/icons-material/Percent';
import { useQuery, useMutation } from '@apollo/client';
import {
  WEDDING_SERVICES_QUERY, CREATE_WEDDING_SERVICE_MUTATION,
  UPDATE_WEDDING_SERVICE_MUTATION, DELETE_WEDDING_SERVICE_MUTATION,
  ALL_SERVICE_CATEGORIES_QUERY,
} from '../graphql/queries';
import ContractedVendorsSection from '../components/ContractedVendorsSection';

const FALLBACK_CATEGORIES = [
  'matchmaking', 'venue', 'catering', 'photography', 'decoration',
  'entertainment', 'staffing', 'transportation', 'beauty', 'honeymoon',
  'astrology', 'rituals', 'invitation', 'accommodation', 'seating',
  'coordination', 'other',
];

const CATEGORY_COLORS = {
  matchmaking: 'secondary', venue: 'primary', catering: 'warning',
  photography: 'info', decoration: 'success', entertainment: 'error',
  staffing: 'default', transportation: 'default', beauty: 'secondary',
  honeymoon: 'primary', astrology: 'info', rituals: 'warning',
  invitation: 'secondary', accommodation: 'info', seating: 'default',
  coordination: 'success', other: 'default',
};

const EMPTY_SERVICE = {
  name: '', category: 'other', description: '', unit: 'per event',
  basePrice: '', taxRate: '18', maxDiscount: '0', isActive: true,
  imageUrl: '', tags: '',
};

function fmt(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

export default function AdminServicesPage() {
  const [filterCat, setFilterCat] = useState('');
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(EMPTY_SERVICE);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });
  const [delConfirm, setDelConfirm] = useState(null);

  const { data, loading, error, refetch } = useQuery(WEDDING_SERVICES_QUERY, {
    variables: { activeOnly: false },
    fetchPolicy: 'network-only',
  });

  const { data: catData } = useQuery(ALL_SERVICE_CATEGORIES_QUERY, {
    variables: { activeOnly: true },
    fetchPolicy: 'cache-and-network',
  });

  const SERVICE_CATEGORIES = (catData?.allServiceCategories || []).map(c => c.name);
  const categoryDisplayMap = (catData?.allServiceCategories || []).reduce((acc, c) => { acc[c.name] = c.displayName; return acc; }, {});
  const categoryColorMap = (catData?.allServiceCategories || []).reduce((acc, c) => { acc[c.name] = c.color || 'default'; return acc; }, {});
  const effectiveCategories = SERVICE_CATEGORIES.length > 0 ? SERVICE_CATEGORIES : FALLBACK_CATEGORIES;

  const [createService, { loading: creating }] = useMutation(CREATE_WEDDING_SERVICE_MUTATION, {
    onCompleted: () => { setDlgOpen(false); refetch(); toast('Service created'); },
    onError: (e) => toast(e.message, 'error'),
  });
  const [updateService, { loading: updating }] = useMutation(UPDATE_WEDDING_SERVICE_MUTATION, {
    onCompleted: () => { setDlgOpen(false); refetch(); toast('Service updated'); },
    onError: (e) => toast(e.message, 'error'),
  });
  const [deleteService] = useMutation(DELETE_WEDDING_SERVICE_MUTATION, {
    onCompleted: () => { setDelConfirm(null); refetch(); toast('Service deleted'); },
    onError: (e) => toast(e.message, 'error'),
  });

  function toast(msg, severity = 'success') {
    setSnack({ open: true, msg, severity });
  }

  function openNew() {
    setEditItem(null);
    setForm(EMPTY_SERVICE);
    setDlgOpen(true);
  }

  function openEdit(svc) {
    setEditItem(svc);
    setForm({
      name: svc.name, category: svc.category, description: svc.description || '',
      unit: svc.unit, basePrice: String(svc.basePrice), taxRate: String(svc.taxRate),
      maxDiscount: String(svc.maxDiscount), isActive: svc.isActive,
      imageUrl: svc.imageUrl || '', tags: svc.tags || '',
    });
    setDlgOpen(true);
  }

  function handleSave() {
    const input = {
      name: form.name.trim(),
      category: form.category,
      description: form.description.trim() || null,
      unit: form.unit.trim() || 'per event',
      basePrice: parseFloat(form.basePrice) || 0,
      taxRate: parseFloat(form.taxRate) ?? 18,
      maxDiscount: parseFloat(form.maxDiscount) ?? 0,
      isActive: form.isActive,
      imageUrl: form.imageUrl.trim() || null,
      tags: form.tags.trim() || null,
    };
    if (editItem) {
      updateService({ variables: { input: { id: editItem.id, ...input } } });
    } else {
      createService({ variables: { input } });
    }
  }

  const services = data?.weddingServices || [];
  const filtered = filterCat ? services.filter(s => s.category === filterCat) : services;

  const summaryByCategory = effectiveCategories.reduce((acc, cat) => {
    const list = services.filter(s => s.category === cat && s.isActive);
    if (list.length) acc[cat] = list.length;
    return acc;
  }, {});

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <RoomServiceIcon color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Wedding Services Catalog</Typography>
            <Typography variant="body2" color="text.secondary">
              Define services & pricing that clients can select for their contracts
            </Typography>
          </Box>
        </Stack>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>
          Add Service
        </Button>
      </Stack>

      {/* Summary cards */}
      <Stack direction="row" flexWrap="wrap" gap={1.5} mb={3}>
        {Object.entries(summaryByCategory).map(([cat, count]) => (
          <Card
            key={cat}
            onClick={() => setFilterCat(filterCat === cat ? '' : cat)}
            sx={{
              cursor: 'pointer', minWidth: 110, borderRadius: 2,
              border: filterCat === cat ? '2px solid' : '1px solid',
              borderColor: filterCat === cat ? 'primary.main' : 'divider',
              bgcolor: filterCat === cat ? 'primary.50' : 'background.paper',
            }}
          >
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary" textTransform="capitalize">
                {categoryDisplayMap[cat] || cat}
              </Typography>
              <Typography variant="h6" fontWeight={700}>{count}</Typography>
            </CardContent>
          </Card>
        ))}
        {filterCat && (
          <Button size="small" onClick={() => setFilterCat('')} sx={{ alignSelf: 'center' }}>
            Clear filter
          </Button>
        )}
      </Stack>

      {/* Registered vendors with a signed contract for the selected category */}
      {filterCat && <ContractedVendorsSection key={filterCat} category={filterCat} />}

      {/* Table */}
      {loading && <CircularProgress sx={{ display: 'block', mx: 'auto', mt: 4 }} />}
      {error && <Alert severity="error">{error.message}</Alert>}
      {!loading && !error && (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 700 }}>Service Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Unit</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Base Price (₹)</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Tax Rate</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Max Discount</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No services found. Click "Add Service" to create one.
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((svc) => (
                <TableRow key={svc.id} hover>
                  <TableCell>
                    <Stack>
                      <Typography variant="body2" fontWeight={600}>{svc.name}</Typography>
                      {svc.description && (
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 260 }}>
                          {svc.description}
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={categoryDisplayMap[svc.category] || svc.category}
                      size="small"
                      color={categoryColorMap[svc.category] || CATEGORY_COLORS[svc.category] || 'default'}
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{svc.unit}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600}>₹{fmt(svc.basePrice)}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Chip label={`${svc.taxRate}%`} size="small" variant="outlined" icon={<PercentIcon sx={{ fontSize: '14px !important' }} />} />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color={svc.maxDiscount > 0 ? 'success.main' : 'text.disabled'}>
                      {svc.maxDiscount > 0 ? `up to ${svc.maxDiscount}%` : 'None'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={svc.isActive ? 'Active' : 'Inactive'}
                      color={svc.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(svc)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => setDelConfirm(svc)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={dlgOpen} onClose={() => setDlgOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editItem ? 'Edit Service' : 'New Service'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Service Name *"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
            />
            <Stack direction="row" spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Category *</InputLabel>
                <Select
                  value={form.category}
                  label="Category *"
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                >
                  {effectiveCategories.map((c) => (
                    <MenuItem key={c} value={c} sx={{ textTransform: 'capitalize' }}>{categoryDisplayMap[c] || c}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Unit"
                value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}
                placeholder="per event / per head / per day"
                fullWidth
              />
            </Stack>
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              multiline rows={2} fullWidth
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Base Price (₹) *"
                type="number"
                value={form.basePrice}
                onChange={(e) => setForm({ ...form, basePrice: e.target.value })}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                fullWidth
              />
              <TextField
                label="Tax Rate (%)"
                type="number"
                value={form.taxRate}
                onChange={(e) => setForm({ ...form, taxRate: e.target.value })}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                fullWidth
                helperText="GST / applicable tax"
              />
              <TextField
                label="Max Discount (%)"
                type="number"
                value={form.maxDiscount}
                onChange={(e) => setForm({ ...form, maxDiscount: e.target.value })}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                fullWidth
              />
            </Stack>
            <TextField
              label="Tags (comma-separated)"
              value={form.tags}
              onChange={(e) => setForm({ ...form, tags: e.target.value })}
              placeholder="catering, veg, premium"
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                />
              }
              label="Active (visible to clients)"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!form.name || !form.basePrice || creating || updating}
          >
            {creating || updating ? <CircularProgress size={18} /> : (editItem ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!delConfirm} onClose={() => setDelConfirm(null)} maxWidth="xs">
        <DialogTitle>Delete Service?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <b>{delConfirm?.name}</b>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDelConfirm(null)}>Cancel</Button>
          <Button
            color="error" variant="contained"
            onClick={() => deleteService({ variables: { id: delConfirm.id } })}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
